"use strict";

import React from "react";
import { findDOMNode } from "react-dom";
import { Overlay, Popover } from "react-bootstrap";

import { topSorted, reduceToObject, scoreIsMissing } from "./shared.js";
import { Graph } from "./graph.js";
import { RawScore, ComboScore, ChangeDisplay } from "./components.js";
import { GlyphClose } from "./icons.js";

const BelSchoolYearPopupContent = ({ yearGroup, object }) => {
  function calcChange() {
    const _r = (idx) => object._results[idx];
    let rA = _r(yearGroup[0]._result_index);
    let rB = _r(yearGroup[yearGroup.length - 1]._result_index);
    if (scoreIsMissing(rA) || scoreIsMissing(rB)) return null;
    else return rB.score - rA.score;
  }

  return (
    <>
      <div className="object-name">{object.name}</div>
      <ol className="data-values">
        {yearGroup.map((sy) => (
          <li>
            <RawScore result={object._results[sy._result_index]} />{" "}
            <span className="year-label">{sy.label}</span>
          </li>
        ))}
      </ol>
      {yearGroup.length > 1 && (
        <p className="score-change">
          Change: <ChangeDisplay result={{ _change: calcChange() }} />
        </p>
      )}
    </>
  );
};

export class PopupGraph extends React.PureComponent {
  state = {
    popupPosition: { x: 0, y: 0 },
  };

  updateConfig = () => {
    this.setState({
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    });
  };

  getMarker(level, slug) {
    let objectPath = `${level}/${slug}`;
    let markers = findDOMNode(this).querySelectorAll(
      `g.marker[data-path="${objectPath}"]`,
    );
    return Array.prototype.slice.call(markers, -1)[0];
  }

  lookupObject(level, slug, variant) {
    // NOTE: use of `variant` now deprecated/discouraged (just use the sub-object's slug…)
    // [in the new cult-2024 demographic tab the original "mainObject" may not be around!]
    let { objects } = this.props;
    let mainObject = objects.filter(
      (obj) => obj._level === level && obj.slug === slug,
    )[0];
    return variant ? mainObject._comparisons.byVariant[variant] : mainObject;
  }

  objectsNearEvent(evt, range) {
    let svg = findDOMNode(this).querySelector("svg");
    let svgPt = (x, y) => Object.assign(svg.createSVGPoint(), { x, y });
    let dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    let vec = (a, b) => ({ x: b.x - a.x, y: b.y - a.y });
    let add = (a, b) => ({ x: a.x + b.x, y: a.y + b.y });
    let mul = (s, v) => ({ x: s * v.x, y: s * v.y });
    let dot = (a, b) => a.x * b.x + a.y * b.y;

    let clientToSVG = svg.getScreenCTM().inverse();
    let evtPt = svgPt(evt.clientX, evt.clientY).matrixTransform(clientToSVG);
    let searchRadius = range * clientToSVG.a; // just apply scale factor, assume no skew/rotation

    let candidateNodes;
    let markersOnly = true; // see https://trello.com/c/6m3Q7fcX/74-graph-overlapping-data-point-handling#comment-58b848fe9dcc08f137a1f2c7
    if (markersOnly) {
      candidateNodes = svg.querySelectorAll(".mainDot");
    } else if (0 && svg.getIntersectionList) {
      // this seems a bit "off" on Chrome at least, stick with full checks…
      let rect = Object.assign(svg.createSVGRect(), {
        x: evtPt.x - searchRadius,
        y: evtPt.y - searchRadius,
        width: 2 * searchRadius,
        height: 2 * searchRadius,
      });
      candidateNodes = svg.getIntersectionList(rect, null);
    } else {
      // ~HACK: this lets the logic below do most of the work
      // TODO: pre-filter on getBoundingClientRect otherwise
      candidateNodes = svg.querySelectorAll(
        "[data-path] > line, [data-path] > circle",
      );
    }
    //console.log(candidateNodes)

    // Due to browser bugs and/or our fallback — and also because we tested a rect but want a radius! — `candidateList` may
    // contain some extra objects which aren't actually within our circle of interest. Narrow down list to actual hits only.

    let elementsNearEvent = Array.prototype.filter.call(
      candidateNodes,
      (el) => {
        let elToSVG = el.getScreenCTM().multiply(svg.getScreenCTM().inverse());
        let elScale = elToSVG.a; // NOTE: assumes non-skewed/rotated transform
        let sw = parseFloat(
          window.getComputedStyle(el).getPropertyValue("stroke-width"),
        ); // NOTE: assumes px units
        switch (el.nodeName) {
          case "line": {
            // distance-from-line (minus half of stroke width…)
            // HT http://geomalgorithms.com/a02-_lines.html#Distance-to-Ray-or-Segment
            let linePt0 = svgPt(
              el.x1.animVal.value,
              el.y1.animVal.value,
            ).matrixTransform(elToSVG);
            let linePt1 = svgPt(
              el.x2.animVal.value,
              el.y2.animVal.value,
            ).matrixTransform(elToSVG);
            let lineVec = vec(linePt0, linePt1);
            let targVec = vec(linePt0, evtPt);

            let segPt;
            let c1 = dot(targVec, lineVec);
            if (c1 <= 0) {
              segPt = linePt0;
            } else {
              let c2 = dot(lineVec, lineVec);
              if (c2 <= c1) {
                segPt = linePt1;
              } else {
                segPt = add(linePt0, mul(c1 / c2, lineVec));
              }
            }
            let r = sw * elScale;
            //if (dist(segPt, evtPt) < 15) console.log("elementsNearEvent line", dist(segPt, evtPt), r, searchRadius, segPt, evtPt)
            return dist(segPt, evtPt) - r < searchRadius;
          }
          case "circle": {
            // distance-from-center (minus radius and stroke…)
            let ctr = svgPt(
              el.cx.animVal.value,
              el.cy.animVal.value,
            ).matrixTransform(elToSVG);
            let r = (el.r.animVal.value + sw) * elScale;
            //if (dist(ctr, evtPt) < 15) console.log("elementsNearEvent circle", dist(ctr, evtPt), r, searchRadius)
            return dist(ctr, evtPt) - r < searchRadius;
          }
          default:
            // NOTE: a general implementation would be much harder, needing to handle `path` etc. objects!
            return false;
        }
      },
    );

    if (0) {
      // debugging aids
      let spot = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "circle",
      );
      spot.setAttribute("cx", evtPt.x);
      spot.setAttribute("cy", evtPt.y);
      spot.setAttribute("r", searchRadius);
      spot.classList.add("dbg");
      svg.appendChild(spot);

      let rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      rect.setAttribute("x", evtPt.x - searchRadius);
      rect.setAttribute("y", evtPt.y - searchRadius);
      rect.setAttribute("width", 2 * searchRadius);
      rect.setAttribute("height", 2 * searchRadius);
      //svg.appendChild(rect)

      elementsNearEvent.forEach((el) => el.classList.add("dbg"));
      setTimeout(() => {
        elementsNearEvent.forEach((el) => el.classList.remove("dbg"));
        svg.removeChild(spot);
        //svg.removeChild(rect)
      }, 1e3);
    }

    let uniquePaths = reduceToObject(elementsNearEvent, (el) => [
      el.parentElement.getAttribute("data-path"),
      true,
    ]);
    let clickedIndexes = reduceToObject(elementsNearEvent, (el) => [
      el.parentElement.getAttribute("data-result-idx"),
      true,
    ]);
    let resultIdx = markersOnly ? Object.keys(clickedIndexes)[0] : null; // ~HACK: assume impossible to click markers of more than one year
    return [
      resultIdx,
      Object.keys(uniquePaths).map((path) => {
        let [level, ...slugAndVariant] = path.split("/");
        return this.lookupObject(level, slugAndVariant.join("/"));
      }),
    ];
  }

  selectObject = (level, slug) => {
    let { selectObject, triggerScrollHelper } = this.props;
    selectObject(level, slug);
    if (!slug) return;
    let marker = this.getMarker(level, slug);
    if (marker) triggerScrollHelper(marker);
    else console.warn("Graph made a selection, but marker not found!");
  };

  componentWillMount() {
    this.updateConfig();
  }

  componentDidMount() {
    // c.f. ResizeObserver, https://discourse.wicg.io/t/svg-element-resize-event/687 [both lacking browser support at present]
    window.addEventListener("resize", this.updateConfig);
    this.updateConfig();

    findDOMNode(this).addEventListener("click", this.handleClick, true); // NOTE: capture phase listener!
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateConfig);
    this.updateConfig();

    findDOMNode(this).removeEventListener("click", this.handleClick, true);
  }

  updateClickHelper = (el) => {
    this.clickHelper = el;
  };

  handleClick = (evt) => {
    let { chooseObjects } = this.state;
    let inPopup = evt.target.closest(".bel-sy-popup") !== null;
    if (chooseObjects || inPopup) {
      // let the popover/overlay handle
      return;
    }

    let [resultIdx, objects] = this.objectsNearEvent(evt, 5);
    if (!objects.length) return;
    else if (objects.length === 1) {
      let obj = objects[0];
      this.selectObject(obj._level, obj.slug);
    } else {
      let chooseObjects = topSorted(objects, `-score@${resultIdx}`, null);
      let parentBox = this.clickHelper.parentNode.getBoundingClientRect();
      let popupPosition = {
        x: evt.clientX - parentBox.left,
        y: evt.clientY - parentBox.top,
      };
      this.setState({ chooseObjects, resultIdx, popupPosition });
    }
    evt.stopPropagation();
  };

  render() {
    let MIN_YW = 165;
    let MAX_PW = 350; // (maximum popup width)

    let { survey, activeView, dynamicWidth = true } = this.props;
    let { windowWidth, chooseObjects, resultIdx, popupPosition } = this.state;

    let wideMode = activeView === "trends";
    let pct = wideMode ? 0.75 : 0.25;
    let nyears = wideMode ? survey._fullYears.length : 2;
    let W = Math.max(MIN_YW * nyears, windowWidth * pct);
    if (!dynamicWidth) {
      // ~HACK: discard the above, hardcode new BEL layout…
      W = activeView === "trends-bel" ? 750 : 125;
    }

    let chooserPopup = null;
    if (chooseObjects) {
      let placement =
        popupPosition.x > MAX_PW && popupPosition.x + MAX_PW > W
          ? "left"
          : "right";
      chooserPopup = (
        <Overlay
          show={true}
          placement={placement}
          shouldUpdatePosition={true}
          target={this.clickHelper}
          container={this}
          rootClose={true}
          onHide={() => this.setState({ chooseObjects: null })}
        >
          <Popover id="graph-objects-nearby" className="nearby-objects-popup">
            <table>
              <tbody>
                {chooseObjects.map((d) => (
                  <tr
                    key={`${d._level}/${d.slug}`}
                    onClick={(evt) => {
                      this.setState({ chooseObjects: null });
                      this.selectObject(d._level, d.slug);
                    }}
                  >
                    <td>{d.name}</td>
                    <td>
                      <ComboScore item={d} result={d._results[resultIdx]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Popover>
        </Overlay>
      );
    }

    let belPopups = null;
    if (this.props.activeView === "trends-bel" && this.props.selectedObject) {
      belPopups = survey._schoolYears.map((yearGroup) => (
        <Overlay
          key={this.props.selectedObject.slug}
          show
          placement="top"
          shouldUpdatePosition
          target={() =>
            document.querySelector(
              `.selected-object-popup-anchor[data-syIdx="${yearGroup._idx}"]`,
            )
          }
          // after target updates but before seen much by user…
          onEntering={function (el) {
            // HACK: mark popups that don't have a target
            if (el.style.top === "0px" && el.style.left === "0px") {
              // WORKAROUND: using dataset inseat of classList to avoid getting overwritten
              el.dataset["noAnchor"] = true;
            } else {
              delete el.dataset["noAnchor"];
            }
          }}
          container={this}
          rootClose={true}
          onHide={() => this.setState({ chooseObjects: null })}
        >
          <Popover id={`graph-data-${yearGroup._idx}`} className="bel-sy-popup">
            <button
              type="button"
              className="close-button"
              onClick={(evt) => {
                this.selectObject(null, null);
              }}
            >
              <GlyphClose />
            </button>
            <BelSchoolYearPopupContent
              yearGroup={yearGroup}
              object={this.props.selectedObject}
            />
          </Popover>
        </Overlay>
      ));
    }

    return (
      <div className="graph-wrapper" style={{ position: "relative" }}>
        <Graph
          {...this.props}
          selectObject={this.selectObject}
          puzzleMarkers={false}
          config={{ W }}
        />
        <div
          ref={this.updateClickHelper}
          className="click-position-helper"
          style={{
            pointerEvents: "none",
            position: "absolute",
            width: "1px",
            height: "1px",
            top: popupPosition.y + "px",
            left: popupPosition.x + "px",
          }}
        />
        {chooserPopup || belPopups}
      </div>
    );
  }
}
